import type { GlossarEintrag } from '@/lib/api'

/**
 * Zerlegt den Dialekttext in Teile, von denen einige einen Glossareintrag
 * tragen.
 *
 * Bewusst eine reine Funktion ohne DOM: Markierungslogik ist fehleranfällig
 * (Überlappungen, Teilwörter, Groß-/Kleinschreibung) und gehört getestet, nicht
 * in eine Komponente gemischt. Und weil hier nichts als HTML zusammengesetzt
 * wird, kann auch nichts eingeschleust werden.
 */

export interface Teil {
  text: string
  eintrag?: GlossarEintrag
}

const WORTZEICHEN = /[\p{L}\p{N}]/u

function istWortgrenze(text: string, index: number): boolean {
  if (index < 0 || index >= text.length) return true
  return !WORTZEICHEN.test(text[index] ?? '')
}

export function zerlegeMitGlossar(text: string, eintraege: GlossarEintrag[]): Teil[] {
  const kandidaten = eintraege
    .filter((eintrag) => eintrag.wort.trim().length > 0)
    // Längere zuerst, damit „koa Lust" nicht von „koa" zerschnitten wird.
    .sort((a, b) => b.wort.length - a.wort.length)

  if (kandidaten.length === 0) return [{ text }]

  const belegt: (GlossarEintrag | undefined)[] = new Array<GlossarEintrag | undefined>(
    text.length,
  ).fill(undefined)
  const kleinText = text.toLocaleLowerCase('de-DE')

  for (const eintrag of kandidaten) {
    const wort = eintrag.wort.trim().toLocaleLowerCase('de-DE')
    let ab = 0
    for (;;) {
      const treffer = kleinText.indexOf(wort, ab)
      if (treffer < 0) break
      ab = treffer + wort.length

      if (!istWortgrenze(text, treffer - 1) || !istWortgrenze(text, treffer + wort.length)) continue
      // Überlappungen gewinnt der längere Eintrag, der schon dran war.
      if (belegt.slice(treffer, treffer + wort.length).some(Boolean)) continue

      for (let i = treffer; i < treffer + wort.length; i += 1) belegt[i] = eintrag
    }
  }

  const teile: Teil[] = []
  let laufend = ''
  let aktuell: GlossarEintrag | undefined

  for (let i = 0; i < text.length; i += 1) {
    const hier = belegt[i]
    if (hier !== aktuell) {
      if (laufend) teile.push(aktuell ? { text: laufend, eintrag: aktuell } : { text: laufend })
      laufend = ''
      aktuell = hier
    }
    laufend += text[i]
  }
  if (laufend) teile.push(aktuell ? { text: laufend, eintrag: aktuell } : { text: laufend })

  return teile
}
